import React, { useState, useRef } from "react";
import { Button, Modal, Tabs, Tab } from "react-bootstrap";
import { ActionHandlerFunction, IActionHandlerTyped, AdminUserBase, UserListData, UserProfile, IActionHandler } from "@preveil-api";
import { Account, AdminUIActionTypes, AdminWarningMessages, UserListActionTypes } from "src/common";
import { UserList, Icon } from "src/components";
import { DetailsCard } from ".";

type AllProps = {
  org_users: AdminUserBase[];
  current_account: Account;
  show_modal: boolean;
  handleAction: ActionHandlerFunction;
}

// Description: Add users modal component for the cloud lock settings page
function AddUsersModalComponent(props: AllProps) {
  const { org_users, current_account, show_modal, handleAction } = props;
  const [ selected_users, setSelectedUsers ] = useState<UserProfile[]>([]);
  const user_list_ref = useRef<{ handlePageActions: ActionHandlerFunction }>();

  function handleShowModal() {
    handleAction({ actionType: AdminUIActionTypes.ShowModal });
    reset();
  }

  // Description: This function is used to update the current selected users list
  function handleUpdateUsers(action_obj: IActionHandlerTyped<UserListData>) {
    const params = action_obj.params;
    const { current_contacts } = params;
    setSelectedUsers(current_contacts);
  }

  // Description: This function is used to remove a user from the current selected users list
  function handleRemoveUser(user: UserProfile) {
    if (!!user_list_ref.current) {
      user_list_ref.current.handlePageActions({
        actionType: UserListActionTypes.RemoveProfile,
        params: user,
      });
    }
  }

  // Description: This function is used to reset the current selected users list in the modal only
  function reset() {
    if (!!user_list_ref.current) { 
      user_list_ref.current.handlePageActions({ actionType: UserListActionTypes.Reset });
    }
    setSelectedUsers([]);
  }

  // Description: Object containing the functions to handle the delete and save actions
  const AddUsersExceptionListRequests = {
    handleDelete: (param: string[]) => {
      const [user_id] = param;
      const user_to_remove = selected_users.filter((user: UserProfile) => user.address === user_id)[0];
      handleRemoveUser(user_to_remove);
    },
    handleSave: () => {
      const users_to_add = selected_users.map((user: UserProfile) => user.address);
      handleAction({ actionType: AdminUIActionTypes.AddUserIdToSyncPermissionList, params: users_to_add });
    }
  };

  function saveUsersToSyncPermissionList() { 
    const users_to_add_to_permission_list = selected_users.map((user: UserProfile) => user.address);
    handleAction({ actionType: AdminUIActionTypes.AddUserIdToSyncPermissionList, params: users_to_add_to_permission_list });
    reset();
  }
  
  // Description: Handle actions at the page level.
  function handlePageActions(action_obj: IActionHandler) {
    const callback = `handle${action_obj.actionType}`;
    if ((AddUsersExceptionListRequests as any)[callback] instanceof Function) {
      (AddUsersExceptionListRequests as any)[callback](action_obj.params);
    }
  }

  return <Modal
    size="lg"
    show={show_modal}
    onHide={handleShowModal}
    aria-label="Sync Permissions"
    centered>
    <Modal.Header closeButton>
      <Modal.Title>Sync Permissions</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <Tabs defaultActiveKey="enter_users">
        <Tab eventKey="enter_users" title="Add Users">
          <div className="align-center justify-content-between">
            <p className="mb-1">Targeted users can be granted sync permissions below:</p>
            <Icon tooltip={AdminWarningMessages.admin_sync_cloud_lock} className="icon-warning" />
          </div>
          <UserList
            ref={user_list_ref}
            current_account={current_account}
            handleAction={handleUpdateUsers}
            hide_badges={true}
            org_users={org_users}
            user_ids={selected_users.map((user) => user.address)}
            placeholder="Start typing to add a user"
            show_external_users={false}
          />
          { selected_users.length > 0 && <DetailsCard
            users={selected_users}
            handleAction={handlePageActions}/>
          }
          <div className="btn-panel mt-4">
            <Button disabled={selected_users.length === 0} type="submit" onClick={saveUsersToSyncPermissionList}>Save</Button>
            <Button variant="no-outline-primary" onClick={reset} disabled={selected_users.length === 0}>Reset</Button>
            <Button variant="no-outline-primary" onClick={handleShowModal}>Cancel</Button>
          </div>
        </Tab>
      </Tabs>
    </Modal.Body>
  </Modal>;
}

export default React.memo(AddUsersModalComponent);